import { useEffect, useRef, useState } from 'react'
import { changeReceptionistBooking, claimReceptionistBooking, getReceptionistBookings, releaseReceptionistClaim, type ReceptionistBooking, type ReceptionistBookings as BookingResult } from './lib/communications'
import { useCrmIdentity } from './lib/crmIdentity'

export function BookingChange({booking,pending,onChange}:{booking:ReceptionistBooking;pending:boolean;onChange:(action:'RESCHEDULE'|'CANCEL',startsAt:string|null,endsAt:string|null)=>Promise<void>}) {
  const [time,setTime]=useState('')
  const [cancel,setCancel]=useState(false)
  if(!booking.canEdit||booking.state==='CANCELLED')return null
  return <form aria-label="Change phone appointment" onSubmit={event=>{
    event.preventDefault()
    if(pending)return
    if(cancel){void onChange('CANCEL',null,null);return}
    const start=new Date(time).getTime(),duration=Date.parse(booking.endsAt)-Date.parse(booking.startsAt)
    if(!Number.isFinite(start)||duration<=0)return
    void onChange('RESCHEDULE',new Date(start).toISOString(),new Date(start+duration).toISOString())
  }}>
    <p>Changes remain pending until Outlook confirms them.</p>
    <label>New appointment time (your local time)<input aria-label="New appointment time (your local time)" type="datetime-local" value={time} required={!cancel} disabled={pending||cancel} onChange={event=>setTime(event.target.value)} /></label>
    <label><input type="checkbox" checked={cancel} disabled={pending} onChange={event=>setCancel(event.target.checked)} /> Cancel this appointment</label>
    <button style={{minHeight:44}} disabled={pending||!cancel&&!time} type="submit">{cancel?'Request cancellation':'Request time change'}</button>
  </form>
}

export function ReceptionistBookings() {
  const { workspaceId, userId } = useCrmIdentity()
  const scope = `${workspaceId}:${userId}`
  const currentScope = useRef(scope); currentScope.current = scope
  const revision = useRef(0)
  const [result,setResult] = useState<{scope:string;value:BookingResult|null}>({scope:'',value:null})
  const [action,setAction] = useState<{scope:string;pending:boolean;error:string;blocked?:boolean}>({scope:'',pending:false,error:''})
  const changeIdentity=useRef<{fingerprint:string;id:string}|null>(null)
  const changeBusy=useRef(false)
  useEffect(() => {
    let active=true
    const refresh=()=>{const request=++revision.current;void getReceptionistBookings().then(value=>{if(active&&request===revision.current){setResult({scope,value});setAction(prior=>prior.scope===scope&&prior.blocked?{...prior,blocked:false}:prior)}}).catch(()=>{if(active&&request===revision.current)setResult({scope,value:null})})}
    refresh();window.addEventListener('focus',refresh)
    const timer=window.setInterval(refresh,15000)
    return()=>{active=false;window.clearInterval(timer);window.removeEventListener('focus',refresh)}
  },[scope])
  const value=result.scope===scope?result.value:null
  const pending=action.scope===scope&&(action.pending||!!action.blocked)
  async function edit(booking:ReceptionistBooking,kind:'RESCHEDULE'|'CANCEL',startsAt:string|null,endsAt:string|null) {
    if(pending||changeBusy.current||!booking.canEdit)return
    const canSubmit=()=>currentScope.current===scope
    const fingerprint=JSON.stringify([scope,booking.id,booking.version,kind,startsAt,endsAt])
    if(changeIdentity.current?.fingerprint!==fingerprint)changeIdentity.current={fingerprint,id:crypto.randomUUID()}
    changeBusy.current=true;setAction({scope,pending:true,error:''})
    try{
      const next=await changeReceptionistBooking(booking,changeIdentity.current.id,kind,startsAt,endsAt,canSubmit)
      if(canSubmit()){revision.current++;setResult({scope,value:next});setAction({scope,pending:false,error:''})}
    }catch{if(canSubmit())setAction({scope,pending:false,blocked:true,error:'Change status is unknown. Wait for the booking to refresh before trying again.'})}
    finally{changeBusy.current=false}
  }
  async function change(booking:ReceptionistBooking,release=false) {
    if(!value||pending||!value.personalBinding&&!release)return
    const canSubmit=()=>currentScope.current===scope
    setAction({scope,pending:true,error:''})
    try {
      const next=release&&booking.claim?await releaseReceptionistClaim(booking.claim,canSubmit):await claimReceptionistBooking(booking,value.personalBinding!,crypto.randomUUID(),canSubmit)
      if(canSubmit()){revision.current++;setResult({scope,value:next})}
    }catch{if(canSubmit())setAction({scope,pending:false,error:'Claim could not be confirmed. Refresh to check the booking before trying again.'});return}
    if(canSubmit())setAction({scope,pending:false,error:''})
  }
  if(!value?.enabled)return null
  return <section aria-label="Receptionist bookings">
    <h3>Phone and receptionist bookings</h3>
    <p>Monday–Friday, 9:00 am–5:00 pm America/Chicago. Appointment duration and calendar verification remain configurable.</p>
    {!value.personalBinding&&<p>Your personal booking calendar is not yet verified.</p>}
    {action.scope===scope&&action.error&&<p role="alert">{action.error}</p>}
    {!value.bookings.length&&<p>No confirmed bookings are visible.</p>}
    {value.bookings.map(booking=><div key={booking.id}>
      <p>{new Date(booking.startsAt).toLocaleString()} - {booking.state==='CANCELLED'?'Cancellation confirmed':booking.bindingRole==='PERSONAL'?'On personal calendar':booking.state==='ASSIGNED'?'Calendar transfer confirmed':booking.claim?'Claimed - calendar transfer pending':'On main calendar - unclaimed'}</p>
      {booking.publicRequest&&<p>{booking.publicRequest.name} · {booking.publicRequest.callbackMode==='TEAM_CALLS_VISITOR'?`Call the visitor at ${booking.publicRequest.phone}`:'Visitor calls the AvertSignal main number'}</p>}
      {booking.syncState&&<p role="status">Calendar change: {booking.syncState.toLowerCase().replaceAll('_',' ')}. The shown time is the last confirmed appointment.</p>}
      <BookingChange booking={booking} pending={pending} onChange={(kind,start,end)=>edit(booking,kind,start,end)} />
      {booking.bindingRole!=='PERSONAL'&&!booking.claim&&booking.state==='CONFIRMED'&&<button disabled={pending||!value.personalBinding||booking.canEdit===false} onClick={()=>void change(booking)}>Claim for my calendar</button>}
      {booking.claim?.state==='RESERVED'&&booking.claim.claimantUserId===userId&&<button disabled={pending} onClick={()=>void change(booking,true)}>Release unsubmitted claim</button>}
    </div>)}
  </section>
}
